import { useState, type ComponentProps } from "react";

import {
  useE2EAgentRunCheckSummary,
  type E2EAgentRunCheckSummary,
} from "../api/admin";
import { Button, Status } from "./ui";
import { CHECK_SEVERITY_PRESENTATION as SEVERITY_PRESENTATION } from "../lib/runChecks";
import RunCheckDetails from "./RunCheckDetails";

type CheckResult = E2EAgentRunCheckSummary["xlm"];
type SeverityCount = NonNullable<CheckResult["severity_counts"]>[number];
type StatusTone = NonNullable<ComponentProps<typeof Status>["tone"]>;

function failureSeverities(result: CheckResult): SeverityCount[] {
  if (result.failure_count == null || result.failure_count === 0) return [];
  if (result.severity_counts?.length) return result.severity_counts;
  return [{ severity: "unclassified", count: result.failure_count }];
}

function resultTone(
  result: CheckResult,
  severities: SeverityCount[],
): StatusTone {
  const tones = severities.map(
    ({ severity }) => SEVERITY_PRESENTATION[severity].tone,
  );
  if (tones.includes("danger")) return "danger";
  if (
    tones.includes("warning") ||
    (result.error_count ?? 0) > 0 ||
    result.status === "skipped"
  )
    return "warning";
  return result.status === "passed" ? "success" : "neutral";
}

export function RunCheckSummaryDetails({
  summary,
}: {
  summary: E2EAgentRunCheckSummary;
}) {
  return (
    <dl className="diagnostic-grid">
      {(["xlm", "dfo"] as const).map((system) => {
        const result = summary[system];
        const failures = result.failure_count;
        const errors = result.error_count ?? 0;
        const skipped = result.status === "skipped";
        const severities = failureSeverities(result);
        return (
          <div key={system}>
            <dt>{system.toUpperCase()} check failures</dt>
            <dd title="Recorded findings, including warnings; checker execution errors are counted separately.">
              <Status tone={resultTone(result, severities)}>
                {failures != null
                  ? failures.toLocaleString()
                  : skipped
                    ? "Skipped"
                    : "Unavailable"}
              </Status>
              {errors > 0 && (
                <span>
                  {" "}
                  · {errors.toLocaleString()} checker error
                  {errors === 1 ? "" : "s"}
                </span>
              )}
              {skipped && failures != null && <span> · Checks skipped</span>}
              {severities.length > 0 && (
                <div
                  className="e2e-run-check-severities"
                  role="group"
                  aria-label={`${system.toUpperCase()} failure levels`}
                >
                  {severities.map(({ severity, count }) => (
                    <Status
                      key={severity}
                      tone={SEVERITY_PRESENTATION[severity].tone}
                    >
                      {count.toLocaleString()}{" "}
                      {SEVERITY_PRESENTATION[severity].label}
                    </Status>
                  ))}
                </div>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export default function RunCheckSummaryPanel({
  runId,
  terminal,
}: {
  runId: string;
  terminal: boolean;
}) {
  const query = useE2EAgentRunCheckSummary(runId, terminal);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const expanded = expandedRunId === runId;
  const hasFailures =
    terminal &&
    query.data &&
    [query.data.xlm, query.data.dfo].some(
      (result) =>
        (result.failure_count ?? 0) > 0 ||
        (result.error_count ?? 0) > 0 ||
        result.status === "findings" ||
        result.status === "error",
    );
  return (
    <section className="e2e-admin-evidence" aria-label="Run checks">
      <header>
        <h3>Run checks</h3>
        {hasFailures && (
          <Button
            tone="quiet"
            size="sm"
            aria-expanded={expanded}
            onClick={() => setExpandedRunId(expanded ? null : runId)}
          >
            {expanded ? "Hide failed checks" : "Show failed checks"}
          </Button>
        )}
      </header>
      {!terminal ? (
        <div className="state">Check results are pending.</div>
      ) : query.isPending ? (
        <div className="state">Loading check results…</div>
      ) : query.error ? (
        <div className="state err" role="alert">
          Check results could not be loaded.{" "}
          <Button tone="quiet" size="sm" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </div>
      ) : query.data ? (
        <RunCheckSummaryDetails summary={query.data} />
      ) : null}
      {hasFailures && expanded && <RunCheckDetails key={runId} runId={runId} />}
    </section>
  );
}
